import { c as _c } from "react/compiler-runtime";
import Grid from '@precis-digital/kurama/Grid';
import { styled, useTheme } from '@precis-digital/kurama/utils';
import Typography from '@precis-digital/kurama/Typography';
import { AreaChart } from '@precis-digital/kurama/charts/AreaChart';
import { useTranslation } from 'shared/translations';
import NumberFormat from 'shared/components/NumberFormat';
import Block from 'shared/components/Block';
import PercentageChip from 'shared/components/PercentageChip';
import { useCurrentClient } from 'shared/context/ClientContext';
import NumberSummaryTooltip from 'shared/components/NumberSummaryTooltip';
import { type ProgressOverTimeRecords } from './ProgressOvertime';
import { objectKeys } from 'shared/utils';
import toolsIcon from 'public/assets/images/tools.svg';
import ThemedImage from 'shared/components/Themed/Image';
import { type ChannelGroupingSummaryData } from 'src/home/transformations/channelGrouping';
import { type ReportingBaseSummaryData } from 'src/home/transformations/reportingBase';
import { type Metric } from 'src/home/reportTemplateParameters';
import { METRICS_WITH_REVERSED_COLOR_SCHEME } from 'src/home/constants';
import { type RefObject } from 'react';
export type SummaryData = ChannelGroupingSummaryData | ReportingBaseSummaryData;
interface SummaryBlockProps {
  startDate: string;
  endDate: string;
  summaryData: SummaryData | null;
  summaryDataForPreviousTimePeriod: SummaryData | null;
  progressOverTimeRecords: Partial<Record<Extract<keyof SummaryData, Metric>, ProgressOverTimeRecords>>;
  previousTimePeriod: [string, string];
  errorBody?: React.ReactNode;
  scorecardGroupRef?: RefObject<HTMLDivElement | null>;
  registerScorecardElement?: (metricId: Metric, element: HTMLDivElement | null) => void;
}
const Summary = t0 => {
  const $ = _c(15);
  const {
    startDate,
    endDate,
    progressOverTimeRecords,
    summaryData,
    summaryDataForPreviousTimePeriod,
    previousTimePeriod,
    errorBody,
    scorecardGroupRef,
    registerScorecardElement
  } = t0;
  const theme = useTheme();
  const {
    t
  } = useTranslation("home");
  const {
    currentClient: t1
  } = useCurrentClient();
  const {
    currency: targetCurrencyCode
  } = t1;
  let t2;
  if ($[0] !== endDate || $[1] !== errorBody || $[2] !== previousTimePeriod || $[3] !== progressOverTimeRecords || $[4] !== registerScorecardElement || $[5] !== startDate || $[6] !== summaryData || $[7] !== summaryDataForPreviousTimePeriod || $[8] !== t || $[9] !== targetCurrencyCode || $[10] !== theme) {
    t2 = objectKeys(progressOverTimeRecords).map(metric => {
      const progressOverTime = progressOverTimeRecords[metric];
      const reverseColorScheme = METRICS_WITH_REVERSED_COLOR_SCHEME.includes(metric);
      return <Grid item={true} xs={true} minWidth={objectKeys(progressOverTimeRecords).length % 3 === 1 ? "50%" : "33.333%"} key={metric}><StyledBlock marginTop="0px" ref={element => {
          registerScorecardElement?.(metric, element);
        }}><Typography variant="h5">{t(metric)}</Typography>{progressOverTime != null && progressOverTime.length > 0 ? <><Grid width="99%" height="60px" marginBottom="12px" marginTop="20px"><AreaChart getConfigByDataKey={_temp} data={progressOverTime} xAxis={{
                hide: true
              }} yAxis={{
                hide: true
              }} /></Grid>{summaryData != null && summaryDataForPreviousTimePeriod != null && <StyledNumberWrapper><Typography variant="h3" style={{
                flex: " 1 1 0",
                display: "flex"
              }}><NumberFormat value={summaryData[metric]} currencyCode={targetCurrencyCode} id={metric} compact={true} /></Typography><NumberSummaryTooltip presentValue={summaryData[metric]} previousValue={summaryDataForPreviousTimePeriod[metric]} previousTimePeriod={previousTimePeriod} timePeriod={[startDate, endDate]} currencyCode={targetCurrencyCode} id={metric} title={t(metric)} reverseColorScheme={reverseColorScheme}><PercentageChip presentValue={summaryData[metric]} previousValue={summaryDataForPreviousTimePeriod[metric]} reverseColorScheme={reverseColorScheme} /></NumberSummaryTooltip></StyledNumberWrapper>}</> : <StyledGrid container={true} direction="column" padding={theme.spacing(4)} alignItems="center" justifyContent="center" textAlign="center"><ThemedImage src={toolsIcon} alt="Tools" width={40} height={40} />{errorBody ?? <Typography variant="h5">{t("noReportDataAvailable")}</Typography>}</StyledGrid>}</StyledBlock></Grid>;
    });
    $[0] = endDate;
    $[1] = errorBody;
    $[2] = previousTimePeriod;
    $[3] = progressOverTimeRecords;
    $[4] = registerScorecardElement;
    $[5] = startDate;
    $[6] = summaryData;
    $[7] = summaryDataForPreviousTimePeriod;
    $[8] = t;
    $[9] = targetCurrencyCode;
    $[10] = theme;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== scorecardGroupRef || $[13] !== t2) {
    t3 = <Grid ref={scorecardGroupRef} container={true} aria-label="performace summary" spacing={2} marginTop="6px" marginBottom="16px" borderRadius="16px">{t2}</Grid>;
    $[12] = scorecardGroupRef;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
};
export default Summary;
const StyledNumberWrapper = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  width: '100%',
  h3: {
    marginTop: '0.15rem'
  }
}));
const StyledGrid = styled(Grid)(({
  theme
}) => ({
  backgroundColor: theme.tokens.background.main.pressed,
  borderRadius: theme.borderRadius.medium,
  marginTop: theme.spacing(4),
  '& > img': {
    marginBottom: theme.spacing(3)
  }
}));
const StyledBlock = styled(Block)(({
  theme
}) => ({
  marginBottom: 0,
  height: '100%'
}));
function _temp(dataKey) {
  return {
    hasGradient: true
  };
}